/**
 * Repository access (ADR 029): the tenant's git routing rules, its own Git tokens, and "test a URL".
 * Everything goes through the shared client, so the session cookie and CSRF echo are applied in one place.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { del, get, post, put } from './client'
import type {
  DeploymentGitRouting,
  GitRoutingRule,
  GitRoutingTestResult,
  ListGitCredentialsResponse,
  ListGitRoutingRulesResponse,
  UpsertGitRoutingRuleRequest,
} from '@/types/gitRouting'

export const gitRoutingKeys = {
  all: ['gitRouting'] as const,
  rules: () => [...gitRoutingKeys.all, 'rules'] as const,
  deployment: () => [...gitRoutingKeys.all, 'deployment'] as const,
  credentials: () => [...gitRoutingKeys.all, 'credentials'] as const,
}

// ── rules ────────────────────────────────────────────────────────────────────────────────────────

export function useGitRoutingRules() {
  return useQuery({
    queryKey: gitRoutingKeys.rules(),
    queryFn: () => get<ListGitRoutingRulesResponse>('/git-routing/rules'),
  })
}

/** The operator's own rules and host defaults, shown read-only below the tenant's. */
export function useDeploymentGitRouting() {
  return useQuery({
    queryKey: gitRoutingKeys.deployment(),
    queryFn: () => get<DeploymentGitRouting>('/git-routing/deployment'),
    staleTime: 5 * 60_000,
  })
}

export function useUpsertGitRoutingRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: UpsertGitRoutingRuleRequest) => post<GitRoutingRule>('/git-routing/rules', request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: gitRoutingKeys.rules() }),
  })
}

export function useDeleteGitRoutingRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<void>(`/git-routing/rules/${encodeURIComponent(id)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: gitRoutingKeys.rules() }),
  })
}

/** Every one of the tenant's rule ids, once each, in their new order. */
export function useReorderGitRoutingRules() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => put<void>('/git-routing/rules/order', { ids }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: gitRoutingKeys.rules() }),
  })
}

/** A live, read-only provider call: which rule matches, which credential, and can it read the repository. */
export function useTestGitRoutingUrl() {
  return useMutation({
    mutationFn: (url: string) => post<GitRoutingTestResult>('/git-routing/test', { url }),
  })
}

// ── credentials ──────────────────────────────────────────────────────────────────────────────────

export function useGitCredentials() {
  return useQuery({
    queryKey: gitRoutingKeys.credentials(),
    queryFn: () => get<ListGitCredentialsResponse>('/git-credentials'),
  })
}

/** A rule's status column reads the credentials, so a token change refreshes both. */
function invalidateCredentialViews(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: gitRoutingKeys.credentials() }),
    queryClient.invalidateQueries({ queryKey: gitRoutingKeys.rules() }),
  ])
}

/**
 * Sets or replaces a token. The value goes in the body only, and `gcTime: 0` drops the mutation — whose
 * variables hold the token — from the cache as soon as it settles rather than keeping it for five minutes.
 */
export function useSetGitCredential() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ name, value }: { name: string; value: string }) =>
      put<void>(`/git-credentials/${encodeURIComponent(name)}`, { value }),
    gcTime: 0,
    onSuccess: () => invalidateCredentialViews(queryClient),
  })
}

export function useRemoveGitCredential() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (name: string) => del<void>(`/git-credentials/${encodeURIComponent(name)}`),
    onSuccess: () => invalidateCredentialViews(queryClient),
  })
}
